import * as THREE from 'three';
import type { Enemy } from '../ai/enemy';
import type { EnemyKind } from '../ai/archetypes';
import type { Projectile, ProjectilePool } from './projectiles';
import { applyDamage, type Health } from './damage';
import { SUPER_CONSTANTS, type SuperMeter } from './super';
import { withinEngagementRange } from './spatial';
import { resolveEnemyKill, type KillLootContext } from './kill';
import type { PickupPool } from '../loot/pickup';
import type { MeleeSwing } from './weapon';

const PLAYER_HIT_INVULN_MS = 250; // brief i-frames after ANY hit lands on the player — stops one lob + one rat bite double-dipping the same instant

export interface DamageEvent {
  killed: boolean;
  scoreValue: number;
  crit: boolean;
  /** S07: the struck enemy's world position at the moment of the hit — optional and additive (every pre-S07 consumer that ignores it is unaffected) so main.ts can play a genuinely positional hit/kill sfx instead of a player-anchored one. */
  position?: THREE.Vector3;
  /** S11: the struck enemy's archetype kind, read synchronously at the same moment `position` is — optional and additive (same discipline as `position` above) — so main.ts's hunt-board kill tracking can attribute a kill to the right target without needing a second lookup once these events are returned. */
  kind?: EnemyKind;
}

/** Player bullets/pellets vs. enemies. Deactivates the projectile on the first enemy it touches (none of S04's 3 weapons pierce). Kills route through `combat/kill.ts`'s single choke point right here, at the moment `takeDamage` reports lethal. */
export function resolveProjectileHitsOnEnemies(active: Projectile[], enemies: readonly Enemy[], pool: ProjectilePool, pickups: PickupPool, loot: KillLootContext, superMeter: SuperMeter): DamageEvent[] {
  const events: DamageEvent[] = [];
  for (const proj of active) {
    if (proj.owner !== 'player') continue;
    for (const enemy of enemies) {
      if (enemy.state === 'dead') continue;
      if (!withinEngagementRange(enemy.position, proj.position, enemy.archetype.radius + proj.radius)) continue;
      const knockDir = proj.velocity.clone().setY(0);
      const killed = enemy.takeDamage(proj.damage, knockDir, proj.knockbackForce);
      pool.deactivate(proj);
      if (killed) resolveEnemyKill(enemy, pickups, loot, superMeter);
      events.push({ killed, scoreValue: enemy.archetype.scoreValue, crit: proj.crit, position: enemy.position.clone(), kind: enemy.archetype.kind });
      break;
    }
  }
  return events;
}

/**
 * The player's melee weapon (Stickball Bat) vs. every enemy within its arc —
 * resolved INSTANTLY off the `MeleeSwing` `combat/weapon.ts` already fire-
 * time-captured (damage/crit baked at the moment the swing was thrown, per
 * §0.7), so there's nothing left to re-derive here. `facing` gates the arc
 * the same way `combat/autoaim.ts`'s cone preset gates targeting — a swing
 * only lands on what's actually in front of the player, not a 360° pulse.
 */
export function resolveMeleePlayerHits(swing: MeleeSwing, enemies: readonly Enemy[], playerPos: THREE.Vector3, facing: number, pickups: PickupPool, loot: KillLootContext, superMeter: SuperMeter): DamageEvent[] {
  const events: DamageEvent[] = [];
  const forward = new THREE.Vector3(Math.sin(facing), 0, Math.cos(facing));
  for (const enemy of enemies) {
    if (enemy.state === 'dead') continue;
    if (!withinEngagementRange(enemy.position, playerPos, swing.range + enemy.archetype.radius)) continue;
    const toEnemy = new THREE.Vector3().subVectors(enemy.position, playerPos).setY(0);
    if (toEnemy.lengthSq() > 1e-6 && toEnemy.angleTo(forward) > swing.arcHalfAngle) continue;
    const knockDir = toEnemy.lengthSq() > 1e-6 ? toEnemy : forward.clone();
    const killed = enemy.takeDamage(swing.damage, knockDir, swing.knockbackForce);
    if (killed) resolveEnemyKill(enemy, pickups, loot, superMeter);
    events.push({ killed, scoreValue: enemy.archetype.scoreValue, crit: swing.crit, position: enemy.position.clone(), kind: enemy.archetype.kind });
  }
  return events;
}

/**
 * Enemy lob projectiles vs. the player. `slideInvulnerable` is the
 * controller's OWN i-frame flag (`PlayerCapsule.state.invulnerable`, set
 * during a slide/dodge) — a SEPARATE mechanism from `playerHealth.
 * invulnerableT` (the post-hit grace window `applyDamage` itself grants).
 * Both must gate every player-damage path or the slide's advertised
 * i-frames silently do nothing against S02 combat (a real, confirmed bug —
 * S02's own review). Returns true if any hit actually landed (i.e. the
 * player wasn't invulnerable by EITHER mechanism) — NOT `applyDamage`'s own
 * return value, which only signals a LETHAL hit.
 */
export function resolveProjectileDamageOnPlayer(
  active: Projectile[],
  playerPos: THREE.Vector3,
  playerRadius: number,
  playerHealth: Health,
  pool: ProjectilePool,
  slideInvulnerable: boolean,
): boolean {
  if (slideInvulnerable) return false; // the dodge fully evades — a projectile that would've hit just continues past, untouched, matching a real dodge rather than merely no-op'ing the damage
  let hit = false;
  for (const proj of active) {
    if (proj.owner !== 'enemy') continue;
    if (!withinEngagementRange(proj.position, playerPos, playerRadius + proj.radius)) continue;
    const wasVulnerable = playerHealth.invulnerableT <= 0 && playerHealth.hp > 0;
    const knockDir = proj.velocity.clone().setY(0);
    applyDamage(playerHealth, proj.damage, { knockbackDir: knockDir, knockbackForce: 4, invulnerableAfterMs: PLAYER_HIT_INVULN_MS });
    pool.deactivate(proj);
    if (wasVulnerable) hit = true;
  }
  return hit;
}

const MELEE_PLAYER_KNOCKBACK = 5;

/**
 * Melee enemies' active attack frame vs. the player. The "am I still close
 * enough" gate is fire-time-captured inside `Enemy.update()` itself (only a
 * genuinely-in-range attack ever lands in `attackers`) — this function's own
 * job is just applying that already-validated hit, gated on the SAME
 * `slideInvulnerable` flag `resolveProjectileDamageOnPlayer` uses (see its
 * docstring). Returns true only for hits that actually landed.
 */
export function resolveMeleeAttacksOnPlayer(attackers: Enemy[], playerPos: THREE.Vector3, playerHealth: Health, slideInvulnerable: boolean): boolean {
  if (slideInvulnerable) return false;
  let hit = false;
  for (const enemy of attackers) {
    const wasVulnerable = playerHealth.invulnerableT <= 0 && playerHealth.hp > 0;
    const knockDir = new THREE.Vector3().subVectors(playerPos, enemy.position).setY(0);
    applyDamage(playerHealth, enemy.archetype.contactDamage, { knockbackDir: knockDir, knockbackForce: MELEE_PLAYER_KNOCKBACK, invulnerableAfterMs: PLAYER_HIT_INVULN_MS });
    if (wasVulnerable) hit = true;
  }
  return hit;
}

/**
 * The super blast: a radial hit centered on the player, per PLAN-CITY §5.
 * Kills route through the same `combat/kill.ts` choke point every other
 * damage path uses (loot still drops, hitstop still arms) — but deliberately
 * WITHOUT a `superMeter` argument, so consuming the whole meter for one
 * blast can't partially refund itself from its own kills.
 */
export function applySuperBlast(playerPos: THREE.Vector3, enemies: readonly Enemy[], pickups: PickupPool, loot: KillLootContext): DamageEvent[] {
  const events: DamageEvent[] = [];
  for (const enemy of enemies) {
    if (enemy.state === 'dead') continue;
    if (!withinEngagementRange(enemy.position, playerPos, SUPER_CONSTANTS.radius)) continue;
    const dir = new THREE.Vector3().subVectors(enemy.position, playerPos).setY(0);
    const killed = enemy.takeDamage(SUPER_CONSTANTS.damage, dir, SUPER_CONSTANTS.knockback);
    if (killed) resolveEnemyKill(enemy, pickups, loot);
    events.push({ killed, scoreValue: enemy.archetype.scoreValue, crit: false, position: enemy.position.clone(), kind: enemy.archetype.kind });
  }
  return events;
}

const LOB_ARC_GRAVITY = 9;

/** Ballistic solve for an enemy lob: given start/end position and a fixed horizontal speed, derive the initial velocity so the projectile arrives at the target after `flightTime`. */
export function computeLobVelocity(origin: THREE.Vector3, target: THREE.Vector3, horizSpeed: number): { velocity: THREE.Vector3; flightTime: number } {
  const from = origin.clone().setY(origin.y + 0.9);
  const to = target.clone().setY(target.y + 1.0);
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const horizDist = Math.hypot(dx, dz);
  const flightTime = Math.max(0.35, horizDist / horizSpeed);
  const vx = dx / flightTime;
  const vz = dz / flightTime;
  const dy = to.y - from.y;
  // Standard projectile-motion solve for initial vy given gravity, start/end height, and flight time.
  const vy = dy / flightTime + 0.5 * LOB_ARC_GRAVITY * flightTime;
  return { velocity: new THREE.Vector3(vx, vy, vz), flightTime };
}

export const LOB_GRAVITY = LOB_ARC_GRAVITY;
